import {useState} from 'react'
import {useMutation} from '@tanstack/react-query'
import {Sparkles, ArrowDownCircle, ArrowUpCircle, AlertTriangle} from 'lucide-react'
import {Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {Alert, AlertDescription, AlertTitle} from '@/components/ui/alert'
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import type {ImpactAnalysisResponse, ImpactItemResponse} from '@/services/types'

const LIFECYCLE_STATES = ['ACTIVE', 'SUSPENDED', 'TERMINATED'] as const
const LIFECYCLE_ANY = '__any__'

interface ImpactAnalysisDialogProps {
    ruleId: string
    open: boolean
    onOpenChange: (open: boolean) => void
}

export function ImpactAnalysisDialog({ruleId, open, onOpenChange}: ImpactAnalysisDialogProps) {
    const i18n = useI18n()
    const services = useServices()

    const [identityTypeCode, setIdentityTypeCode] = useState('')
    const [lifecycleState, setLifecycleState] = useState<string>(LIFECYCLE_ANY)
    const [maxIdentities, setMaxIdentities] = useState<string>('1000')
    const [result, setResult] = useState<ImpactAnalysisResponse | null>(null)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)

    const mutation = useMutation({
        mutationFn: async () => {
            const trimmedType = identityTypeCode.trim()
            const parsedMax = Number.parseInt(maxIdentities, 10)
            return services.autoAssignRules.runImpactAnalysis(ruleId, {
                filter: {
                    identityTypeCode: trimmedType.length > 0 ? trimmedType : null,
                    lifecycleState: lifecycleState === LIFECYCLE_ANY ? null : lifecycleState,
                },
                maxIdentities: Number.isFinite(parsedMax) ? parsedMax : null,
            })
        },
        onSuccess: (response) => {
            setResult(response)
            setErrorMessage(null)
        },
        onError: (err: Error) => {
            setResult(null)
            setErrorMessage(err.message || i18n.autoAssignRules.impactAnalysis.unknownError)
        },
    })

    const isLoading = mutation.isPending

    const handleOpenChange = (next: boolean) => {
        if (!next) {
            mutation.reset()
            setResult(null)
            setErrorMessage(null)
        }
        onOpenChange(next)
    }

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="max-w-4xl" data-testid="impact-analysis-dialog">
                <DialogHeader>
                    <DialogTitle data-testid="impact-analysis-title">
                        <Sparkles className="inline mr-2 h-5 w-5"/>{i18n.autoAssignRules.impactAnalysis.title}
                    </DialogTitle>
                    <DialogDescription>{i18n.autoAssignRules.impactAnalysis.description}</DialogDescription>
                </DialogHeader>

                <div className="grid gap-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div className="grid gap-1">
                            <Label htmlFor="impact-identity-type">{i18n.autoAssignRules.impactAnalysis.identityTypeCode}</Label>
                            <Input
                                id="impact-identity-type"
                                data-testid="impact-input-identity-type"
                                placeholder={i18n.autoAssignRules.impactAnalysis.identityTypeCodePlaceholder}
                                value={identityTypeCode}
                                onChange={(e) => setIdentityTypeCode(e.target.value)}
                            />
                        </div>
                        <div className="grid gap-1">
                            <Label htmlFor="impact-lifecycle">{i18n.autoAssignRules.impactAnalysis.lifecycleState}</Label>
                            <Select value={lifecycleState} onValueChange={setLifecycleState}>
                                <SelectTrigger id="impact-lifecycle" data-testid="impact-select-lifecycle">
                                    <SelectValue/>
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={LIFECYCLE_ANY}>{i18n.autoAssignRules.impactAnalysis.lifecycleStateAny}</SelectItem>
                                    {LIFECYCLE_STATES.map((s) => (
                                        <SelectItem key={s} value={s}>{s}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid gap-1">
                            <Label htmlFor="impact-max">{i18n.autoAssignRules.impactAnalysis.maxIdentities}</Label>
                            <Input
                                id="impact-max"
                                data-testid="impact-input-max"
                                type="number"
                                min={1}
                                max={10000}
                                value={maxIdentities}
                                onChange={(e) => setMaxIdentities(e.target.value)}
                            />
                            <span className="text-xs text-muted-foreground">{i18n.autoAssignRules.impactAnalysis.maxIdentitiesHint}</span>
                        </div>
                    </div>

                    <div>
                        <Button
                            data-testid="btn-run-impact-analysis"
                            onClick={() => mutation.mutate()}
                            disabled={isLoading}
                        >
                            {isLoading ? i18n.autoAssignRules.impactAnalysis.running : i18n.autoAssignRules.impactAnalysis.runButton}
                        </Button>
                    </div>

                    {errorMessage && (
                        <Alert variant="destructive" data-testid="impact-error">
                            <AlertTitle>{i18n.autoAssignRules.impactAnalysis.errorTitle}</AlertTitle>
                            <AlertDescription>{errorMessage}</AlertDescription>
                        </Alert>
                    )}

                    {result && (
                        <div className="grid gap-3">
                            <div className="text-sm" data-testid="impact-counts">
                                {i18n.autoAssignRules.impactAnalysis.counts({
                                    evaluated: result.totalEvaluated,
                                    add: result.additionCount,
                                    remove: result.removalCount,
                                    unchanged: result.unchangedCount,
                                })}
                            </div>

                            {result.truncated && (
                                <Alert variant="default" data-testid="impact-truncated">
                                    <AlertTriangle className="h-4 w-4"/>
                                    <AlertDescription>
                                        {i18n.autoAssignRules.impactAnalysis.truncatedWarning}
                                    </AlertDescription>
                                </Alert>
                            )}

                            {result.items.length === 0 ? (
                                <Alert data-testid="impact-empty">
                                    <AlertDescription>
                                        {i18n.autoAssignRules.impactAnalysis.empty}
                                    </AlertDescription>
                                </Alert>
                            ) : (
                                <div className="max-h-96 overflow-auto border rounded">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead data-testid="col-identity">{i18n.autoAssignRules.impactAnalysis.tableIdentity}</TableHead>
                                                <TableHead data-testid="col-profile">{i18n.autoAssignRules.impactAnalysis.tableProfile}</TableHead>
                                                <TableHead data-testid="col-action">{i18n.autoAssignRules.impactAnalysis.tableAction}</TableHead>
                                                <TableHead data-testid="col-source-rule">{i18n.autoAssignRules.impactAnalysis.tableSourceRule}</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {result.items.map((item: ImpactItemResponse, i: number) => (
                                                <TableRow key={`${item.identityId}-${item.profileId}-${item.action}-${i}`} data-testid={`impact-result-row-${i}`}>
                                                    <TableCell>{item.identityDisplayName}</TableCell>
                                                    <TableCell>{item.profileDisplayName}</TableCell>
                                                    <TableCell>
                                                        {item.action === 'ADD' ? (
                                                            <Badge variant={BadgeVariant.SUCCESS} data-testid={`impact-action-add-${i}`}>
                                                                <ArrowUpCircle className="inline h-3 w-3 mr-1"/>
                                                                {i18n.autoAssignRules.impactAnalysis.actionAdd}
                                                            </Badge>
                                                        ) : (
                                                            <Badge variant={BadgeVariant.DESTRUCTIVE} data-testid={`impact-action-remove-${i}`}>
                                                                <ArrowDownCircle className="inline h-3 w-3 mr-1"/>
                                                                {i18n.autoAssignRules.impactAnalysis.actionRemove}
                                                            </Badge>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>{item.sourceRuleName ?? '-'}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => handleOpenChange(false)} data-testid="btn-impact-close">
                        {i18n.autoAssignRules.impactAnalysis.close}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
